import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from '@/utils/icons';
import { cn } from '@/lib/utils';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'dls-fixed dls-inset-0 dls-z-50 dls-bg-white/30 dls-backdrop-blur-sm data-[state=open]:dls-animate-in data-[state=closed]:dls-animate-out data-[state=closed]:dls-fade-out-0 data-[state=open]:dls-fade-in-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'dls-fixed dls-inset-0 dls-z-50 dls-grid dls-w-full dls-h-[100dvh] dls-gap-4 dls-border-0 dls-bg-background dls-p-6 dls-shadow-40 dls-duration-200 data-[state=open]:dls-animate-in data-[state=closed]:dls-animate-out data-[state=closed]:dls-fade-out-0 data-[state=open]:dls-fade-in-0 data-[state=closed]:dls-zoom-out-95 data-[state=open]:dls-zoom-in-95 md:dls-inset-auto md:dls-left-[50%] md:dls-top-[50%] md:dls-translate-x-[-50%] md:dls-translate-y-[-50%] md:dls-h-auto md:dls-max-h-[85vh] md:dls-w-[calc(100vw-3rem)] md:dls-max-w-[30rem] md:dls-rounded-xl md:dls-border lg:dls-max-w-[60vw] lg:dls-min-w-[40rem]',
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="dls-absolute dls-right-4 dls-top-4 dls-rounded-sm dls-opacity-70 dls-ring-offset-background dls-transition-opacity hover:dls-opacity-100 focus:dls-outline-none focus:dls-ring-2 focus:dls-ring-ring focus:dls-ring-offset-2 disabled:dls-pointer-events-none data-[state=open]:dls-bg-accent data-[state=open]:dls-text-muted-foreground">
        <X className="dls-h-4 dls-w-4" />
        <span className="dls-sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('dls-flex dls-flex-col dls-space-y-1.5 dls-text-center sm:dls-text-left', className)} {...props} />
);

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('dls-text-lg dls-font-semibold dls-leading-none dls-tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('dls-text-sm dls-text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('dls-flex dls-flex-col-reverse sm:dls-flex-row sm:dls-justify-end sm:dls-space-x-2', className)}
    {...props}
  />
);

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
